import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import EmailInvites from "@src/components/spaces/EmailInvites";
import ShareableLink from "@src/components/spaces/ShareableLink";
import PublicPageLinks from "@src/components/spaces/PublicPageLinks";
import FormField from "@src/components/spaces/FormField";
import {
  InviteMethod,
  getStoredInviteMethod,
  storeInviteMethod,
} from "@src/components/spaces/inviteMethodStorage";
import { Select } from "@src/components/spaces/ui";
import { formatSessionDateTime, formatSessionTime } from "@src/utils/format";
import { useEvent } from "@src/queries/events";

// Named for what the scheduler is choosing rather than for how OpenUp works.
const TABS: Array<{ value: InviteMethod; label: string }> = [
  { value: "email", label: "Direct invite" },
  { value: "link", label: "Company link" },
  { value: "public", label: "Public landing page" },
];

const sessionLabel = (startsAt: string, endsAt: string): string =>
  `${formatSessionDateTime(startsAt)}–${formatSessionTime(endsAt)}`;

/**
 * Owns the invite-method tab for one session. Keyed on `sessionId` by the caller, so switching
 * sessions remounts this and the initializer re-reads that session's stored tab. Deriving the tab
 * that way rather than syncing it in an effect means a session switch never renders the previous
 * session's tab first, and the per-session draft state inside the two panels resets with it.
 */
const InvitePanel = ({
  sessionId,
  eventId,
}: {
  sessionId: string;
  eventId: string;
}) => {
  const [method, setMethod] = useState<InviteMethod>(
    () => getStoredInviteMethod(sessionId) ?? "email",
  );

  const selectMethod = (next: InviteMethod) => {
    setMethod(next);
    storeInviteMethod(sessionId, next);
  };

  return (
    <>
      <div
        role="tablist"
        className="mb-6 inline-flex rounded-full bg-gray-100 p-1"
      >
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={method === tab.value}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              method === tab.value
                ? "bg-white text-indigo-800 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => selectMethod(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {method === "email" && <EmailInvites sessionId={sessionId} />}
      {method === "link" && <ShareableLink sessionId={sessionId} />}
      {method === "public" && <PublicPageLinks eventId={eventId} />}
    </>
  );
};

const EventInviteesPage = () => {
  const { id = "" } = useParams();
  const [selectedId, setSelectedId] = useState("");
  const { data: event, isLoading } = useEvent(id);

  const sessions = event?.sessions ?? [];
  // Invites are per session; default to the first until the user picks one.
  const sessionId = selectedId || sessions[0]?.id || "";

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to={`/events/${id}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to event
      </Link>

      <h1 className="mb-2 text-2xl font-semibold text-indigo-800">
        Invite people
      </h1>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {!isLoading && sessions.length > 0 && (
        <>
          {sessions.length > 1 ? (
            <div className="mb-6">
              <FormField label="Session">
                {({ id: selectId }) => (
                  <Select
                    id={selectId}
                    value={sessionId}
                    onChange={(e) => setSelectedId(e.target.value)}
                  >
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.title} ·{" "}
                        {sessionLabel(session.startsAt, session.endsAt)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <p className="mt-1.5 text-sm text-gray-500">
                Each session has its own invitees and link. Invitees register
                for the session you choose here.
              </p>
            </div>
          ) : (
            <p className="mb-6 text-sm text-gray-600">
              {sessions[0].title} ·{" "}
              {sessionLabel(sessions[0].startsAt, sessions[0].endsAt)}
            </p>
          )}

          {/* Keyed so a session switch remounts the tab state and both panels' drafts. */}
          <InvitePanel key={sessionId} sessionId={sessionId} eventId={id} />
        </>
      )}
    </div>
  );
};

export default EventInviteesPage;
